import { Link } from "react-router-dom";
import { Monitor, Clapperboard, ArrowRight, Globe2, MessageCircle, UserCheck } from "lucide-react";
import { Reveal, CountUp } from "../components/motion";
import { IMG, MARKETS_STAT, WHY, PROCESS } from "../data/content";

export const TrustStrip = () => (
  <section className="py-14" data-testid="trust-strip">
    <div className="container-x grid grid-cols-1 sm:grid-cols-3 gap-5">
      {[
        { icon: Globe2, big: <CountUp end={MARKETS_STAT} />, label: "markets served worldwide" },
        { icon: MessageCircle, big: "Direct", label: "WhatsApp response — no ticket queues" },
        { icon: UserCheck, big: "New studio", label: "hands-on founder access on every project" },
      ].map((s, i) => (
        <Reveal key={i} delay={i * 0.12}>
          <div className="card p-6 flex items-center gap-5 h-full" data-testid={`trust-chip-${i}`}>
            <s.icon size={26} className="text-[#00D9C0] shrink-0" />
            <div>
              <p className="font-heading font-bold text-2xl m-0 leading-tight">{s.big}</p>
              <p className="text-[#A0A3C2] text-sm m-0 mt-1">{s.label}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const ServicesSection = ({ chapter = "01" }) => (
  <section className="py-24" data-testid="services-section">
    <div className="container-x">
      <Reveal>
        <p className="eyebrow mb-3">{chapter} — What we do</p>
        <h2 className="max-w-2xl">Two services. One creative standard.</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-6 mt-14">
        {[
          {
            icon: Monitor, title: "Website Design", img: IMG.web2,
            desc: "Custom-designed, fully responsive websites built to turn visitors into customers — landing pages, business sites and storefronts, designed and delivered remotely.",
            testid: "service-card-web",
          },
          {
            icon: Clapperboard, title: "Video Ad Creation", img: IMG.video1,
            desc: "Short, scroll-stopping ad videos for social platforms and campaigns — scripted, edited and graded to fit your brand and your audience.",
            testid: "service-card-video",
          },
        ].map((s, i) => (
          <Reveal key={s.title} delay={i * 0.15}>
            <Link to="/services" data-testid={s.testid} className="card overflow-hidden no-underline text-[#F5F5FA] flex flex-col h-full group">
              <div className="h-56 overflow-hidden">
                <img src={s.img} alt={`${s.title} sample work`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <s.icon size={28} className="text-[#6C5CE7] mb-4" />
                <h3 className="mb-3">{s.title}</h3>
                <p className="text-[#A0A3C2] text-[0.95rem] leading-relaxed m-0 flex-1">{s.desc}</p>
                <span className="inline-flex items-center gap-2 mt-6 font-heading font-semibold text-[#00D9C0] text-sm">
                  Learn more <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const WhySection = ({ chapter = "02" }) => (
  <section className="py-24 bg-[#1B1D33]/40 border-y border-[rgba(255,255,255,0.05)]" data-testid="why-section">
    <div className="container-x">
      <Reveal>
        <p className="eyebrow mb-3">{chapter} — Why choose us</p>
        <h2 className="max-w-2xl">Small studio. Big reasons.</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {WHY.map((w, i) => (
          <Reveal key={w.t} delay={i * 0.1}>
            <div className="card p-7 h-full" data-testid={`why-card-${i}`}>
              <span className="font-heading font-bold text-[#6C5CE7] text-lg">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 mb-3 text-[1.15rem]">{w.t}</h3>
              <p className="text-[#A0A3C2] text-sm leading-relaxed m-0">{w.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const AboutTeaser = ({ chapter = "03" }) => (
  <section className="py-24" data-testid="about-teaser">
    <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.08)]">
          <img src={IMG.studio} alt="BHF Creative studio desk with camera, storyboards and design tools" loading="lazy" className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="eyebrow mb-3">{chapter} — About the studio</p>
        <h2>A remote studio with a hands-on approach.</h2>
        <p className="text-[#A0A3C2] mt-6 leading-relaxed">
          BHF Creative is a new digital studio built around one idea: small businesses deserve
          the same quality of design and video as big brands — without the agency overhead.
          We work entirely remotely, from first message to final delivery.
        </p>
        <Link to="/about" data-testid="about-teaser-link" className="btn btn-ghost no-underline mt-8">
          Our story <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export const ProcessSection = ({ chapter = "04" }) => (
  <section className="py-24 bg-[#1B1D33]/40 border-y border-[rgba(255,255,255,0.05)]" data-testid="process-section">
    <div className="container-x">
      <Reveal>
        <p className="eyebrow mb-3">{chapter} — How it works</p>
        <h2 className="max-w-2xl">From first message to final delivery.</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
        {PROCESS.map((p, i) => (
          <Reveal key={p.n} delay={i * 0.1}>
            <div className="relative p-6 rounded-xl border border-[rgba(255,255,255,0.07)] h-full" data-testid={`process-step-${i}`}>
              <span className="font-heading font-bold text-4xl grad-text">{p.n}</span>
              <h3 className="text-[1.05rem] mt-4 mb-2">{p.t}</h3>
              <p className="text-[#A0A3C2] text-sm leading-relaxed m-0">{p.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
